import { type MqttSchema, config } from '@/lib/config';
import { FUSED_INPUT_FEEDS, FUSED_TRACKS_TOPIC } from '@/lib/mqtt/legacy/fusedTracks';
import { LEGACY_SUBSCRIPTIONS } from '@/lib/mqtt/subscriptions';
import { outputTopics } from '@/lib/mqtt/topics';
import { aircraftForOi } from '@/lib/oi/aircraftForOi';
import { useDronesStore } from '@/lib/store/drones';
import { useOiStore } from '@/lib/store/oi';
import { useSurvStore } from '@/lib/store/surv';
import type { Context } from '@/lib/store/ui';
import { FUSED_TRACKS_ID } from '@/lib/surv/fusedSummary';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { topicMatchesContext } from './topicGroups';

/** A topic predicate plus a stable `key` that changes whenever the predicate
 *  does (so the feed can reset its buffer on scope change), and the concrete
 *  topics we *expect* for this scope — seeded into the tree as silent
 *  placeholders so "subscribed but not arriving" is visible (e.g. a cooperative
 *  source's `surveillance_sensors/<id>` that never publishes).
 *
 *  "Subscribed" is the operative word — these lists must mirror
 *  `subscriptions.ts`'s `buildSubscriptions()` schema branch exactly. An
 *  `output`-schema tenant never subscribes to legacy flat topics at all
 *  (ticket 86e2jbaqr: the backend deletes those sinks outright), so
 *  seeding them here would show permanently-empty rows for topics we
 *  never listen to — indistinguishable from "subscribed but quiet", which
 *  is a different and misleading claim.
 *
 *  Every helper below takes `schema` as an explicit parameter (default
 *  `config.mqtt.schema`) rather than reading module-level config directly,
 *  so tests can prove the gating for each schema value instead of only
 *  exercising whatever `vitest.config.ts` happens to pin. */

/** Per-aircraft topics on the v1/output namespace (including `waypoint_pred`,
 *  the ETA to the next waypoint, and `waypoint_approached`, each waypoint
 *  reached — the latter is also published tenant-wide on `v1/output/alerts/…`,
 *  which only shows in the overview). Deliberately omits:
 *   - the per-field alias branch (`.../drones/{fieldName}`) — 56 topics
 *     carrying values already present in these two.
 *   - `.../events` — briefly published by the NID/NID-DD jobs before being
 *     corrected to the doc-specified tenant-wide `v1/output/events`
 *     (`outputTopics.tenantEvents`, seeded separately — see
 *     topicGroups.ts). Confirmed dead: 0 messages across every check this
 *     session, vs hundreds on the tenant-wide topic in the same windows. */
function outputAircraftTopics(sn: string, schema: MqttSchema): string[] {
  if (schema === 'legacy') return [];
  return [
    outputTopics.networkid(sn),
    outputTopics.telemetry(sn),
    outputTopics.oiDeclaration(sn),
    outputTopics.waypointPred(sn),
    outputTopics.waypointApproached(sn),
  ];
}

function legacyDroneTopicsFor(id: string, schema: MqttSchema): string[] {
  if (schema === 'output') return [];
  return [`networkid/${id}`, `TISB Stream/${id}`, `flightplan/${id}`];
}

/** The exact topic list a single selected drone gets in the `drones`
 *  context — factored out so the "all aircraft" overview can seed the same
 *  set per known aircraft instead of only the wildcard family. */
function droneTopicsFor(id: string, schema: MqttSchema): string[] {
  return [...legacyDroneTopicsFor(id, schema), ...outputAircraftTopics(id, schema)];
}

/** The exact topic list a single selected OI gets in the `oi` context —
 *  same reasoning as `droneTopicsFor`. */
function oiTopicsFor(id: string, schema: MqttSchema): string[] {
  const legacy = schema === 'output' ? [] : [`flightplan/${id}`, `networkid/${id}`];
  return [...legacy, ...outputAircraftTopics(id, schema)];
}

export interface DebugScope {
  key: string;
  match: (topic: string) => boolean;
  expected: string[];
}

export interface ScopeSelection {
  droneId: string | null;
  survMode: 'fused' | 'diagnostic';
  survNet: string | null;
  /** The AIRCRAFT of the selected OI (its drone_id), not the OI's own id. Every
   *  per-aircraft topic is keyed by the serial, so scoping to the OI's UUID
   *  matched nothing and the panel read "Listening — no data yet" for any
   *  selected OI. Null when no OI is selected or it can't be resolved yet, which
   *  shows the overview. See `aircraftForOi`. */
  oiAircraftId: string | null;
  /** Every aircraft id the app currently tracks (from `useDronesStore`),
   *  regardless of selection. Used ONLY to seed placeholders in the "no
   *  entity selected" overview — a topic-kind that simply hasn't ticked
   *  yet for a known aircraft (e.g. `oi/declaration`, which fires far less
   *  often than `networkid`/`telemetry`) must still show as an empty row,
   *  not be silently absent until its first message happens to land while
   *  the panel is open. Base rule: every subscribed topic is visible
   *  whether or not it has data. */
  knownDroneIds: readonly string[];
}

/** Pure: map the active tab + its current selection to a topic scope. A
 *  specific entity narrows to just its topic(s); otherwise the whole tab's
 *  topics (the "overview / fused" case). Live topics only (Debug is live-only). */
/** Concrete + wildcard subscription topics for a tab (the single broker
 *  subscription list, filtered to this tab) — seeded as silent placeholders so
 *  the whole subscribed surface is visible even before anything arrives.
 *  Reads the REAL live subscription list (`LEGACY_SUBSCRIPTIONS`, computed
 *  once from `config.mqtt.schema` at module load) rather than rebuilding it
 *  from `schema`, since that list IS the actual current subscription state —
 *  `schema` here only threads through to `topicMatchesContext`'s classifier
 *  for test parity with the rest of this file. */
const allTopicsFor = (ctx: Context, schema: MqttSchema): string[] =>
  LEGACY_SUBSCRIPTIONS.filter((t) => topicMatchesContext(t, ctx, schema));

const LIVE_RF_TOPICS = ['v1/ingest/live_rf/REAL_TIME_DATA', 'v1/ingest/live_rf/HEARTBEAT'];

export function buildScope(
  ctx: Context,
  sel: ScopeSelection,
  schema: MqttSchema = config.mqtt.schema,
): DebugScope {
  if (ctx === 'surv') {
    if (sel.survMode === 'diagnostic' && sel.survNet) {
      const net = sel.survNet;
      if (net === FUSED_TRACKS_ID) {
        // The fused feed plus the three raw feeds it is made of (ENG-342).
        const topics = [FUSED_TRACKS_TOPIC, ...FUSED_INPUT_FEEDS.map((f) => `surveillance_traffic/${f}`)];
        return { key: `surv:${net}`, match: (t) => topics.includes(t), expected: topics };
      }
      // Live Drone is fed by the RF ingest topics (ENG-253) as well as the flat paths.
      const extra = net === 'live_drone_rf' ? LIVE_RF_TOPICS : [];
      const base = [`surveillance_traffic/${net}`, `surveillance_sensors/${net}`];
      return {
        key: `surv:${net}`,
        match: (t) => base.includes(t) || extra.includes(t),
        expected: [...base, ...extra],
      };
    }
    return {
      key: 'surv:all',
      match: (t) => topicMatchesContext(t, 'surv', schema),
      expected: allTopicsFor('surv', schema),
    };
  }
  if (ctx === 'drones') {
    if (sel.droneId) {
      const id = sel.droneId;
      // Schema-gated to match subscriptions.ts exactly. Only 'both' (an
      // active parallel-publish window) lists both; a single-schema tenant
      // lists only what it subscribes to.
      //
      // Deliberately NOT included: `v1/output/events` / `v1/output/alerts/+`.
      // Both are tenant-wide — every drone's alerts arrive on the same
      // topic, distinguished only inside the payload's `droneIds[]`, which
      // MQTT topic-name matching can't see. Adding them here would show
      // every OTHER drone's alerts too, defeating the point of narrowing to
      // one aircraft. They're still visible in the "no drone selected"
      // overview below, where showing everything is already the intent.
      const topics = droneTopicsFor(id, schema);
      return {
        key: `drones:${id}`,
        match: (t) => topics.includes(t),
        expected: topics,
      };
    }
    // No drone selected: seed the wildcard subscription families themselves
    // (networkid/#, flysafe/v1/#, TISB Stream/+, traffic_control, flightplan/+)
    // PLUS the concrete per-aircraft topic set for every drone already known
    // (same list `droneTopicsFor` gives a single selected drone). Without
    // this, a topic-kind that hasn't ticked yet for a given aircraft since
    // the panel opened — `oi/declaration` fires far less often than
    // `networkid`/`telemetry` — is silently missing instead of shown empty.
    return {
      key: `drones:all:${schema}:${sel.knownDroneIds.join(',')}`,
      match: (t) => topicMatchesContext(t, 'drones', schema),
      expected: dedupe([
        ...allTopicsFor('drones', schema),
        ...sel.knownDroneIds.flatMap((id) => droneTopicsFor(id, schema)),
      ]),
    };
  }
  if (ctx === 'oi') {
    if (sel.oiAircraftId) {
      const id = sel.oiAircraftId;
      // Schema-gated, same as the drones branch above. Tenant-wide
      // events/alerts excluded here too — topic-name matching can't narrow
      // them to one OI/drone.
      const topics = oiTopicsFor(id, schema);
      return {
        key: `oi:${id}`,
        match: (t) => topics.includes(t),
        expected: topics,
      };
    }
    // Same reasoning as `drones:all` above — an OI's polygon topic
    // (`oi/declaration`) is low-cadence, so it needs seeding per known
    // aircraft too, not just the wildcard family.
    return {
      key: `oi:all:${schema}:${sel.knownDroneIds.join(',')}`,
      match: (t) => topicMatchesContext(t, 'oi', schema),
      expected: dedupe([
        ...allTopicsFor('oi', schema),
        ...sel.knownDroneIds.flatMap((id) => oiTopicsFor(id, schema)),
      ]),
    };
  }
  return { key: 'none', match: () => false, expected: [] };
}

function dedupe(topics: string[]): string[] {
  return [...new Set(topics)];
}

/** Live scope for the active tab, reacting to its store selection. */
export function useDebugScope(ctx: Context): DebugScope {
  const droneId = useDronesStore((s) => s.selectedId);
  const survMode = useSurvStore((s) => s.mode);
  const survNet = useSurvStore((s) => s.selectedNetwork);
  const oiId = useOiStore((s) => s.selectedId);
  // Resolved to a primitive inside the selector, so a telemetry tick that
  // doesn't change the answer doesn't re-render the panel.
  const oiAircraftId = useDronesStore((s) => aircraftForOi(oiId, s.byId));
  // `byId` gets a new object reference on every telemetry tick (each drone
  // updates independently at ~1.7 Hz), but the SET of known ids changes far
  // less often. useShallow keeps this array referentially stable across
  // ticks so the memo below — and therefore useDebugFeed's scope-change
  // reset — doesn't fire on every message.
  const knownDroneIds = useDronesStore(useShallow((s) => Object.keys(s.byId)));
  return useMemo(
    () => buildScope(ctx, { droneId, survMode, survNet, oiAircraftId, knownDroneIds }),
    [ctx, droneId, survMode, survNet, oiAircraftId, knownDroneIds],
  );
}
